import { Button } from '@tvg/fuse/button';
import { Drawer as FuseDrawer } from '@tvg/fuse/drawer';
import { IconChevronRight } from '@tvg/fuse/icon/all';
import { useDrawerState } from 'client/state/Drawer';
import React, { useEffect, useMemo, useRef } from 'react';
import usePortal from 'react-useportal';
import useSSR from 'use-ssr';
import styles from './DrawerInner.module.css';

export type DrawerInnerProps = {
  name: string;
  open: boolean;
  onCloseHandler: () => void;
  onDrawerBack: () => void;
  children?: React.ReactNode;
};

function DrawerInner({ name, open, onCloseHandler, onDrawerBack, children }: DrawerInnerProps) {
  const showViewProducts = useDrawerState((s) => s.showViewProducts);
  const toggleDrawerOpen = useDrawerState((s) => s.toggleDrawerOpen);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { isBrowser } = useSSR();

  const { Portal } = usePortal({
    bindTo: isBrowser ? document.getElementById('drawer--inner') || undefined : undefined,
  });

  useEffect(() => {
    if (open) {
      drawerRef.current?.focus();
      setTimeout(() => {
        toggleDrawerOpen.on();
      }, 100);
    }
  }, [open, toggleDrawerOpen]);

  const stickyDrawer = useMemo(
    () => ({
      container: {
        className: !open ? 'drawer-inner__sticky-drawer__hide-shadow' : '',
        dataTestId: 'drawer-inner-sticky-drawer-data-testid',
        open: showViewProducts,
        transition: '200ms',
        zIndex: '1',
      },
      button: (
        <Button
          dataTestId="drawer-inner-sticky-drawer-button-data-testid"
          fullWidth
          icon={<IconChevronRight />}
          iconRight
          size="large"
          variant="primary"
          onClick={onCloseHandler}
        >
          View Products
        </Button>
      ),
    }),
    [open, onCloseHandler, showViewProducts],
  );

  return (
    <Portal>
      <div className={styles.drawerInner} aria-hidden={!open}>
        <FuseDrawer
          srLabelBackButton="Back to filters"
          srLabelCloseButton={`Close ${name} list`}
          className="modal"
          hasBackButton
          open={open}
          onClose={onCloseHandler}
          title={name}
          backCallback={onDrawerBack}
          zIndex="21"
          ref={drawerRef}
          dataTestId="facet-item-drawer--inner"
          stickyDrawer={stickyDrawer}
        >
          {children}
        </FuseDrawer>
      </div>
    </Portal>
  );
}

export default React.memo(DrawerInner);
